import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import images from "../../utils/imageLoader";
import "../../style/style-components/public-layout.css";

const PublicLayout = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuButtonRef = useRef(null);
    const menuRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.requestAnimationFrame(() => menuRef.current?.querySelector("a")?.focus());

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setMenuOpen(false);
                window.requestAnimationFrame(() => menuButtonRef.current?.focus());
                return;
            }

            if (event.key === "Tab") {
                const menuLinks = Array.from(menuRef.current?.querySelectorAll("a") || []);
                const firstLink = menuLinks[0];
                const lastLink = menuLinks[menuLinks.length - 1];

                if (!firstLink || !lastLink) return;

                if (event.shiftKey && document.activeElement === firstLink) {
                    event.preventDefault();
                    lastLink.focus();
                } else if (!event.shiftKey && document.activeElement === lastLink) {
                    event.preventDefault();
                    firstLink.focus();
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [menuOpen]);

    const openCookiePreferences = () => {
        window.dispatchEvent(new Event("wisecursor:open-cookie-preferences"));
    };

    return (
        <>
            <a className="skip-link" href="#main-content">Skip to main content</a>
            <header className="public-header">
                <div className="public-header__promo">
                    <div className="container"><span>Intro price: 25% off for new customers</span><a href="tel:+12815709294">Sales and customer service: 281-570-9294</a></div>
                </div>
                <div className="public-header__inner container">
                    <div className="public-header__identity">
                        <button
                            aria-controls="public-navigation"
                            aria-expanded={menuOpen}
                            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                            className="public-header__menu-button"
                            onClick={() => setMenuOpen((isOpen) => !isOpen)}
                            ref={menuButtonRef}
                            type="button"
                        >
                            {menuOpen
                                ? <span aria-hidden="true" className="public-header__menu-icon public-header__menu-icon--open" />
                                : <img aria-hidden="true" src={images["figma-home/header-menu-burger.svg"]} alt="" />}
                        </button>
                        <Link aria-label="WiseCursor home" className="public-header__brand" to="/">
                            <img src={images["Footer__WiseCursor-Logo Teal.png"]} alt="WiseCursor Creative Studio" />
                        </Link>
                    </div>
                    <nav
                        aria-label="Primary navigation"
                        className={`public-header__nav${menuOpen ? " public-header__nav--open" : ""}`}
                        id="public-navigation"
                        onClick={() => setMenuOpen(false)}
                        ref={menuRef}
                    >
                        <NavLink to="/projectpage">Works</NavLink>
                        <NavLink to="/services">Services</NavLink>
                        <a href="/#about">About</a>
                        <a href="/#process">Process</a>
                    </nav>
                    <div className="public-header__actions">
                        <a className="public-header__phone" href="tel:+12815709294">281-570-9294</a>
                        <nav aria-label="WiseCursor social media" className="public-header__social-links">
                            <a aria-label="Visit WiseCursor on LinkedIn" href="https://www.linkedin.com/company/wisecursorcreativeagency" rel="noreferrer" target="_blank"><img src={images["Wl-Footer__Social1.svg"]} alt="" /></a>
                            <a aria-label="Visit WiseCursor on Facebook" href="https://www.facebook.com/profile.php?id=100094425924683" rel="noreferrer" target="_blank"><img src={images["Wl-Footer__Social3.svg"]} alt="" /></a>
                        </nav>
                        <a aria-label="Start a consultation" className="public-header__cta" href="/#contact">
                            <span className="public-header__cta-label">Get a consultation</span>
                            <span aria-hidden="true" className="public-header__cta-label--compact">Chat</span>
                        </a>
                    </div>
                </div>
            </header>
            <div id="main-content"><Outlet /></div>
            <footer className="public-footer">
                <img className="public-footer__art" src={images["figma-home/footer-owl.jpg"]} alt="" />
                <div className="public-footer__overlay" aria-hidden="true" />
                <div className="public-footer__inner container">
                    <div className="public-footer__top">
                        <div className="public-footer__brand">
                            <img src={images["Wl-Header__WiseCursor-Logo White.png"]} alt="WiseCursor Creative Studio" />
                            <p>Strategy, design, development, and practical AI-enabled product support.</p>
                        </div>
                        <nav aria-label="Footer navigation" className="public-footer__links">
                            <h2>Quick links</h2>
                            <a href="/#about">About</a>
                            <Link to="/projectpage">Projects</Link>
                            <Link to="/services">Services</Link>
                            <a href="/#contact">Contact</a>
                        </nav>
                        <div className="public-footer__contact">
                            <h2>Get a consultation</h2>
                            <a href="tel:+12815709294">281-570-9294</a>
                            <a href="mailto:info@wisecursor.com">info@wisecursor.com</a>
                            <p>Serving The Colony, Texas and remote teams.</p>
                        </div>
                    </div>
                    <div className="public-footer__bottom">
                        <p>© {new Date().getFullYear()} WiseCursor</p>
                        <div><Link to="/privacy">Privacy</Link><button onClick={openCookiePreferences} type="button">Cookie preferences</button></div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default PublicLayout;
